import { Trans } from '@lingui/react/macro';
import { Plus, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { DayPattern, ShiftSet } from '@/types/template';

interface PatternStarterChoicesProps {
  /**
   * Begins a new pattern: with a pre-filled starter skeleton, or `null` to
   * start from scratch. The shift type is always left empty so the user picks
   * a real one (starter names are display sugar, not resolved type IDs).
   */
  onStart: (pattern: ShiftSet | null) => void;
  /** Opens the bulk Excel import. */
  onImport: () => void;
}

interface Starter {
  id: string;
  title: string;
  subtitle: string;
  dayPattern: Exclude<DayPattern, 'custom'>;
  startTime: string;
  endTime: string;
  count: number;
}

const STARTERS: Starter[] = [
  {
    id: 'bar-evenings',
    title: 'Bar — evenings',
    subtitle: 'Weekdays · 18:00–02:00',
    dayPattern: 'weekdays',
    startTime: '18:00',
    endTime: '02:00',
    count: 2,
  },
  {
    id: 'kitchen-day',
    title: 'Kitchen — full day',
    subtitle: 'Every day · 09:00–17:00',
    dayPattern: 'everyday',
    startTime: '09:00',
    endTime: '17:00',
    count: 2,
  },
  {
    id: 'server-lunch',
    title: 'Server — lunch',
    subtitle: 'Weekdays · 11:00–16:00',
    dayPattern: 'weekdays',
    startTime: '11:00',
    endTime: '16:00',
    count: 3,
  },
  {
    id: 'weekend-service',
    title: 'Weekend service',
    subtitle: 'Weekends · 16:00–23:00',
    dayPattern: 'weekends',
    startTime: '16:00',
    endTime: '23:00',
    count: 3,
  },
];

/**
 * The choices for beginning a new shift pattern: starter cards that pre-fill a
 * pattern (the user still picks the shift type), plus start-from-scratch and
 * Excel-import actions. Shared by the empty-pane {@link PatternEmptyState} and
 * the "Add pattern" modal so both offer the same options.
 */
export function PatternStarterChoices({ onStart, onImport }: PatternStarterChoicesProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-2 text-left">
        {STARTERS.map((starter) => (
          <button
            key={starter.id}
            type="button"
            onClick={() => onStart(toShiftSet(starter))}
            className="rounded-md border p-3 transition-colors hover:border-primary hover:bg-accent"
          >
            <div className="text-sm font-medium">{starter.title}</div>
            <div className="text-xs text-muted-foreground">{starter.subtitle}</div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        <Trans>or</Trans>
        <span className="h-px flex-1 bg-border" />
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        <Button onClick={() => onStart(null)}>
          <Plus className="size-4" />
          <Trans>Start from scratch</Trans>
        </Button>
        <Button variant="outline" onClick={onImport}>
          <Upload className="size-4" />
          <Trans>Import from Excel</Trans>
        </Button>
      </div>
    </div>
  );
}

/** Expands a starter into a full (type-less) `ShiftSet` skeleton for the form. */
function toShiftSet(starter: Starter): ShiftSet {
  return {
    id: '',
    name: starter.title,
    schedule: starter.dayPattern,
    shiftType: { id: '', name: '' },
    startTime: starter.startTime,
    endTime: starter.endTime,
    staffing: { method: 'automatic', count: starter.count, filters: {} },
  };
}
